import React from 'react';
import classNames from 'classnames';

/**
 * Изменения класса <Value/> из react-select, который отвечает за отрисовку одного
 * пункта выбора в <Select/>.
 *
 * Из-за того, что react не всегда корректно обрабатывает utf-ые символы, вывод специальных
 * символов осуществляется через недокументированное свойство dangerouslySetInnerHTML.
 * В самом react-select данный подход используется, чтобы отрисовать символ '&times;'
 * в методе renderClear(), когда в других местах напрямую передаются спецсимволы (непонятно,
 * почему так сделали, выводили бы уже все символы через dangerouslySetInnerHTML).
 *
 * В данном классе переопределён метол renderRemoveIcon, чтобы также корректно
 * отрисовывать символ '&times;'.
 * В методе renderLabel workaround для корректной отрисовки символа '&nbsp;',
 * чтобы не было необходимости переопределять весь класс
 * <Select/>.
 */
export default class SelectValue extends React.Component {
  constructor(props) {
    super(props);
    this.onRemove = this.onRemove.bind(this);
    this.handleTouchEndRemove = this.handleTouchEndRemove.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
  }

  onRemove(event) {
    event.preventDefault();
    event.stopPropagation();
    this.props.onRemove(this.props.value);
  }

  handleMouseDown(event) {
    if (event.type === 'mousedown' && event.button !== 0) {
      return;
    }
    if (this.props.onClick) {
      event.stopPropagation();
      this.props.onClick(this.props.value, event);
      return;
    }
    if (this.props.value.href) {
      event.stopPropagation();
    }
  }

  handleTouchEndRemove(event) {
    // Check if the view is being dragged, In this case
    // we don't want to fire the click event (because the user only wants to scroll)
    if (this.dragging) return;

    // Fire the mouse events
    this.onRemove(event);
  }

  handleTouchMove() {
    // Set a flag that the view is being dragged
    this.dragging = true;
  }

  handleTouchStart() {
    // Set a flag that the view is not being dragged
    this.dragging = false;
  }

  /**
   * Корректно отрисовываем символ удаления.
   */
  renderRemoveIcon() {
    if (this.props.disabled || !this.props.onRemove) return '';
    return (
      <span
        className="Select-value-icon"
        aria-hidden="true"
        onMouseDown={this.onRemove}
        onTouchEnd={this.handleTouchEndRemove}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        dangerouslySetInnerHTML={{ __html: '&times;' }}
      />
    );
  }

  renderLabel() {
    let label = this.props.children;
    /**
     * В списке с множественным выбором в this.props.children передаваётся массив,
     * где 1-ый элемент - это label пункта выбора,
     * 2-ой элемент - это кнопка удаления данного пункта.
     * Данный workaround исправляет кнопку удаления, чтобы она корректно
     * отображала символ '&nbsp;'.
     */
    if (Array.isArray(label)) {
      label = [label[0]];
      if (label.length > 1) {
        label.push(
          <span
            className="Select-aria-only"
            dangerouslySetInnerHTML={{ __html: '&nbsp;' }}
          />
        );
      }
    }
    /* --- Конец workaround-а --- */
    const className = 'Select-value-label';
    return this.props.onClick || this.props.value.href ? (
      <a
        className={className}
        href={this.props.value.href}
        target={this.props.value.target}
        onMouseDown={this.handleMouseDown}
        onTouchEnd={this.handleMouseDown}
      >
        {label}
      </a>
    ) : (
      <span
        className={className}
        role="option"
        aria-selected="true"
        id={this.props.id}
      >{label}</span>
    );
  }

  render() {
    return (
      <div
        className={classNames('Select-value', this.props.value.className)}
        style={this.props.value.style}
        title={this.props.value.title}
      >
        {this.renderRemoveIcon()}
        {this.renderLabel()}
      </div>
    );
  }
}

SelectValue.propTypes = {
  children: React.PropTypes.node,
  disabled: React.PropTypes.bool, // disabled prop passed to ReactSelect
  id: React.PropTypes.string, // Unique id for the value - used for aria
  onClick: React.PropTypes.func, // method to handle click on value label
  onRemove: React.PropTypes.func, // method to handle removal of the value
  value: React.PropTypes.object.isRequired, // the option object for this value
};
